import type { ReactNode } from 'react'

import Link from 'next/link'
import { ChartColumn, ShieldCheck, Smartphone } from 'lucide-react'
import Logo, { LogoMark } from '@/components/generals/logo'

interface AuthLayoutProps {
    title: string
    description: string
    children: ReactNode
    footer: ReactNode
}

const FEATURES = [
    {
        icon: Smartphone,
        title: 'Catat dalam hitungan detik',
        description: 'Formulir ringkas yang nyaman dipakai dari ponsel, kapan saja.'
    },
    {
        icon: ChartColumn,
        title: 'Laporan yang mudah dipahami',
        description: 'Grafik harian, mingguan, dan bulanan menunjukkan ke mana uangmu pergi.'
    },
    {
        icon: ShieldCheck,
        title: 'Data pribadi tetap milikmu',
        description: 'Setiap akun hanya bisa melihat catatan keuangannya sendiri.'
    }
]

function PreviewCard() {
    const bars = [
        { income: 62, expense: 38 },
        { income: 20, expense: 46 },
        { income: 74, expense: 41 },
        { income: 35, expense: 52 },
        { income: 88, expense: 44 },
        { income: 48, expense: 30 }
    ]

    return (
        <div aria-hidden='true' className='relative w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-2xl backdrop-blur-sm'>
            <div className='flex items-center justify-between'>
                <span className='text-xs font-medium text-white/60'>
                    Contoh tampilan dasbor
                </span>

                <span className='rounded-full bg-highlight/20 px-2 py-0.5 text-[0.625rem] font-semibold text-highlight'>
                    Ilustrasi
                </span>
            </div>

            <p className='mt-4 text-xs text-white/60'>
                Total saldo
            </p>

            <p className='mt-1 font-heading text-2xl font-bold tracking-tight text-white tabular-nums'>
                Rp 12.480.000
            </p>

            <div className='mt-5 flex h-24 items-end gap-3 border-b border-white/10 pb-px'>
                {bars.map((bar, index) => (
                    <div key={index} className='flex flex-1 items-end justify-center gap-[3px]'>
                        <span className='w-full max-w-2.5 rounded-t-[3px] bg-[#3BB592]' style={{ height: `${bar.income}%` }} />
                        <span className='w-full max-w-2.5 rounded-t-[3px] bg-expense' style={{ height: `${bar.expense}%` }} />
                    </div>
                ))}
            </div>

            <div className='mt-4 flex flex-col gap-2.5'>
                {[
                    { title: 'Gaji bulanan', amount: '+Rp 8.250.000', income: true },
                    { title: 'Makan siang', amount: '−Rp 32.000', income: false }
                ].map(item => (
                    <div key={item.title} className='flex items-center justify-between rounded-lg bg-white/[0.05] px-3 py-2'>
                        <span className='text-xs font-medium text-white/85'>
                            {item.title}
                        </span>

                        <span className={item.income ? 'text-xs font-semibold text-[#5FD3AE] tabular-nums' : 'text-xs font-semibold text-[#F2A195] tabular-nums'}>
                            {item.amount}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function AuthLayout({ title, description, children, footer }: AuthLayoutProps) {
    return (
        <div className='flex min-h-screen bg-background'>
            <section
                aria-label='Tentang aplikasi'
                className='relative hidden w-[46%] max-w-[640px] flex-col justify-between overflow-hidden bg-navy px-12 py-10 text-white lg:flex xl:px-16'
            >
                <div aria-hidden='true' className='pointer-events-none absolute -top-40 -right-32 size-[28rem] rounded-full bg-[radial-gradient(circle,rgb(241_185_75/0.18),transparent_65%)]' />
                <div aria-hidden='true' className='pointer-events-none absolute -bottom-48 -left-40 size-[32rem] rounded-full bg-[radial-gradient(circle,rgb(22_134_106/0.22),transparent_65%)]' />

                <div className='relative flex items-center gap-3'>
                    <LogoMark className='size-10 [&_rect]:fill-white/10' />

                    <span className='flex flex-col leading-none'>
                        <span className='font-heading text-base font-bold tracking-tight'>
                            Daily Financial
                        </span>

                        <span className='mt-1 text-[0.625rem] font-semibold tracking-[0.14em] text-white/55 uppercase'>
                            Recording System
                        </span>
                    </span>
                </div>

                <div className='relative flex flex-col gap-8 py-10'>
                    <div>
                        <h2 className='max-w-md text-[2rem] leading-[1.15] font-bold text-white xl:text-[2.4rem]'>
                            Kelola uang harianmu dengan lebih tenang.
                        </h2>

                        <p className='mt-4 max-w-md text-[0.9375rem] leading-relaxed text-white/70'>
                            Catat pemasukan dan pengeluaran setiap hari, lalu lihat kondisi keuanganmu lewat ringkasan yang jelas dan rapi.
                        </p>
                    </div>

                    <PreviewCard />
                </div>

                <ul className='relative grid gap-5 xl:grid-cols-3 xl:gap-6'>
                    {FEATURES.map(feature => {
                        const Icon = feature.icon

                        return (
                            <li key={feature.title} className='flex gap-3 xl:flex-col'>
                                <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-highlight'>
                                    <Icon aria-hidden='true' className='size-[1.125rem]' />
                                </span>

                                <span>
                                    <span className='block text-sm font-semibold text-white'>
                                        {feature.title}
                                    </span>

                                    <span className='mt-1 block text-xs leading-relaxed text-white/60'>
                                        {feature.description}
                                    </span>
                                </span>
                            </li>
                        )
                    })}
                </ul>
            </section>

            <main className='flex flex-1 flex-col'>
                <div className='flex items-center justify-between px-5 pt-6 sm:px-10 lg:hidden'>
                    <Link href='/masuk' className='rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/25'>
                        <Logo />
                    </Link>
                </div>

                <div className='flex flex-1 items-center justify-center px-5 py-10 sm:px-10'>
                    <div className='w-full max-w-[420px]'>
                        <h1 className='text-2xl leading-tight font-bold sm:text-[1.75rem]'>
                            {title}
                        </h1>

                        <p className='mt-2 text-sm leading-relaxed text-muted-foreground sm:text-[0.9375rem]'>
                            {description}
                        </p>

                        <div className='mt-8'>
                            {children}
                        </div>

                        <div className='mt-8 border-t border-border pt-6 text-center text-sm text-muted-foreground'>
                            {footer}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    )
}
